export class TreasuryWalletResponseContractError extends Error {
  constructor(detail = 'expected a success envelope with scoped wallet records.') {
    super(`Invalid Treasury wallet response: ${detail}`);
    this.name = 'TreasuryWalletResponseContractError';
    this.code = 'INVALID_API_RESPONSE';
  }
}

const isRecord = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const text = value => typeof value === 'string' && value.length > 0;
const nullableText = value => value === null || typeof value === 'string';
const date = value => value === null || typeof value === 'string' && Number.isFinite(Date.parse(value));

const safe = value => !['privateKey', 'mnemonic', 'seedPhrase', 'password', 'passwordHash', 'accessToken', 'refreshToken'].some(key => Object.hasOwn(value, key));
const isWallet = wallet => isRecord(wallet) && safe(wallet)
  && ['id', 'communityId', 'fundId', 'chain', 'address', 'label'].every(key => text(wallet[key]))
  && nullableText(wallet.fundName) && nullableText(wallet.fundCode) && nullableText(wallet.tokenSymbol)
  && typeof wallet.watchOnly === 'boolean' && typeof wallet.isActive === 'boolean'
  && typeof wallet.explorerUrl === 'string'
  && isRecord(wallet.donationConfig) && safe(wallet.donationConfig)
  && typeof wallet.donationConfig.isDefaultDestination === 'boolean'
  && typeof wallet.donationConfig.isAcceptingContributions === 'boolean'
  && ['internal', 'community', 'public'].includes(wallet.donationConfig.visibility)
  && nullableText(wallet.donationConfig.destinationLabel)
  && typeof wallet.donationConfig.instructions === 'string'
  && nullableText(wallet.donationConfig.qrCodeUrl) && nullableText(wallet.donationConfig.referenceHint)
  && date(wallet.lastSyncedAt)
  && (wallet.lastObservedBalance === null || Number.isFinite(wallet.lastObservedBalance) && wallet.lastObservedBalance >= 0)
  && date(wallet.lastObservedBalanceUpdatedAt)
  && ['idle', 'success', 'partial', 'failed'].includes(wallet.lastSyncStatus)
  && nullableText(wallet.lastSyncError) && nullableText(wallet.lastSyncProvider)
  && date(wallet.lastSuccessfulSyncAt) && date(wallet.createdAt) && date(wallet.updatedAt);

export const readTreasuryWalletListResponse = payload => {
  if (!isRecord(payload) || payload.success !== true || !Array.isArray(payload.data) || !payload.data.every(isWallet)) {
    throw new TreasuryWalletResponseContractError();
  }
  return payload;
};

export const readTreasuryWalletMutationResponse = payload => {
  if (!isRecord(payload) || payload.success !== true || !isWallet(payload.data)) {
    throw new TreasuryWalletResponseContractError('expected a success envelope with the saved wallet record.');
  }
  return payload;
};

const isSyncResult = result => isRecord(result) && safe(result)
  && text(result.walletId) && text(result.provider) && nullableText(result.chain)
  && text(result.walletAddress) && nullableText(result.assetSymbol)
  && (result.observedBalance === null || Number.isFinite(result.observedBalance) && result.observedBalance >= 0)
  && date(result.syncedAt) && Number.isInteger(result.importedTransactionCount) && result.importedTransactionCount >= 0
  && Array.isArray(result.warnings) && result.warnings.every(value => typeof value === 'string')
  && typeof result.success === 'boolean' && nullableText(result.error);

export const readTreasuryWalletSyncResponse = payload => {
  const summary = payload?.data;
  const single = isSyncResult(summary);
  const bulk = isRecord(summary) && safe(summary)
    && ['walletCount', 'syncedCount', 'failedCount', 'importedTransactionCount', 'observedBalanceCount'].every(key => Number.isInteger(summary[key]) && summary[key] >= 0)
    && summary.syncedCount + summary.failedCount === summary.walletCount
    && Array.isArray(summary.warnings) && summary.warnings.every(value => typeof value === 'string')
    && Array.isArray(summary.errors) && summary.errors.every(error => isRecord(error) && safe(error)
      && text(error.walletId) && text(error.walletAddress) && text(error.error))
    && typeof summary.success === 'boolean' && Array.isArray(summary.results)
    && summary.results.length === summary.walletCount && summary.results.every(isSyncResult)
    && summary.failedCount === summary.results.filter(result => result.success === false).length
    && summary.success === (summary.failedCount === 0)
    && summary.importedTransactionCount === summary.results.reduce((sum, result) => sum + result.importedTransactionCount, 0)
    && summary.observedBalanceCount === summary.results.filter(result => result.observedBalance !== null).length;
  if (!isRecord(payload) || payload.success !== true || !(single || bulk)) {
    throw new TreasuryWalletResponseContractError('expected a successful wallet sync result or community summary.');
  }
  return payload;
};
